/**
 * `AISessionUsageTracker.ts` — port of Unity's `AISessionUsageTracker.cs`.
 *
 * Aggregates TOKEN usage per source ("Realtime", "Input Transcription",
 * Responses helpers). Tokens are the unit of record; this file deliberately
 * knows nothing about money.
 *
 * ⚠ DO NOT ADD A PRICE TABLE HERE (removed 2026-10-15). It used to carry
 * USD-per-1M rates per model prefix and multiply them out, and both halves of
 * that were wrong:
 *
 * - The RATES were partly guesses. `gpt-realtime-2.1` — the model the stock
 *   config actually runs — was priced at the 1.5 tier because no published
 *   figure was available, and the `gpt-5.6` audio rates were carried over from
 *   a different family. A hardcoded rate also cannot change without shipping a
 *   release, and nothing recorded WHICH rates produced a given number, so an
 *   old figure could not be told apart from a current one.
 * - The ARITHMETIC double-counted the discount. `cachedInputTokens` is a
 *   SUBSET of `inputTokens`, not a sibling of it, and the cached tokens were
 *   billed at the full input rate AND again at the cached rate. That inflated
 *   every web cost figure ~3x, and up to ~8x on a high-cache turn — worse the
 *   better the cache performed. Unity's tracker subtracts before billing and
 *   was never affected.
 *
 * Costing now happens OUTSIDE the shipped clients, against stored token
 * counts, so a rate correction re-prices history instead of invalidating it.
 * Every usage row carries the model and the raw counts, and the
 * `spatial-transcript` trace keeps the provider's own accounting verbatim.
 * See `documentation/ai-cost-rates.md` in the Scenes-V parent repo (this port is
 * a submodule of it, so the path is `../documentation/` from here).
 */

export interface UsageDelta {
  readonly model: string;
  readonly inputTokens: number;
  readonly outputTokens: number;
  readonly inputAudioTokens?: number;
  readonly outputAudioTokens?: number;
  readonly cachedInputTokens?: number;
}

export interface UsageBucket {
  readonly source: string;
  model: string;
  inputTokens: number;
  outputTokens: number;
  inputAudioTokens: number;
  outputAudioTokens: number;
  /**
   * A SUBSET of {@link inputTokens} that the provider served from cache, not a
   * separate quantity to be added to it. Anything deriving a total from these
   * fields must subtract rather than sum — see the header.
   */
  cachedInputTokens: number;
}

export class AISessionUsageTracker {
  private readonly m_buckets = new Map<string, UsageBucket>();

  add(source: string, delta: UsageDelta): void {
    let bucket = this.m_buckets.get(source);
    if (!bucket) {
      bucket = {
        source,
        model: delta.model,
        inputTokens: 0,
        outputTokens: 0,
        inputAudioTokens: 0,
        outputAudioTokens: 0,
        cachedInputTokens: 0,
      };
      this.m_buckets.set(source, bucket);
    }
    bucket.model = delta.model || bucket.model;
    bucket.inputTokens += delta.inputTokens;
    bucket.outputTokens += delta.outputTokens;
    bucket.inputAudioTokens += delta.inputAudioTokens ?? 0;
    bucket.outputAudioTokens += delta.outputAudioTokens ?? 0;
    bucket.cachedInputTokens += delta.cachedInputTokens ?? 0;
  }

  get buckets(): ReadonlyArray<UsageBucket> {
    return [...this.m_buckets.values()];
  }

  get totalTokens(): number {
    let total = 0;
    for (const b of this.m_buckets.values()) {
      total += b.inputTokens + b.outputTokens + b.inputAudioTokens + b.outputAudioTokens;
    }
    return total;
  }

  get totalCachedInputTokens(): number {
    let total = 0;
    for (const b of this.m_buckets.values()) total += b.cachedInputTokens;
    return total;
  }

  /**
   * Share of text input the provider served from cache, 0-1, or `null` when
   * there has been no text input to have a ratio about.
   *
   * This is the closest thing here to a cost signal and is the reason the
   * summary reports it: input dominates spend, so what a session costs tracks
   * how much of its standing prompt was NOT re-charged at full rate.
   */
  get cacheHitRate(): number | null {
    let input = 0;
    let cached = 0;
    for (const b of this.m_buckets.values()) {
      input += b.inputTokens;
      cached += b.cachedInputTokens;
    }
    if (input <= 0) return null;
    return Math.min(1, cached / input);
  }

  /** One-line summary shown in the panel debug row. */
  get summaryText(): string {
    if (this.m_buckets.size === 0) return "";
    const rate = this.cacheHitRate;
    const tokens = `${this.totalTokens.toLocaleString()} tokens`;
    return rate === null ? tokens : `${tokens} · ${Math.round(rate * 100)}% cached`;
  }

  get breakdownText(): string {
    return this.buckets
      .map(
        (b) =>
          `${b.source} (${b.model}): in ${b.inputTokens + b.inputAudioTokens}, out ${b.outputTokens + b.outputAudioTokens}, cached ${b.cachedInputTokens}`,
      )
      .join("\n");
  }

  reset(): void {
    this.m_buckets.clear();
  }
}
